import { Alert, Box, Divider, List, ListItem, ListItemText, Typography } from '@mui/material'
import React from 'react'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { type ISection } from '../../../../models/TermsSection'

interface CloseSurveyModalProps {
  selectedRow?: ISection | null
  handleCloseModal?: () => void
  // Returning `false` (sync or async) signals the action did not proceed (e.g. it was blocked),
  // so the modal should stay open; any other return value closes it on success.
  onConfirm: (row?: ISection | null) => unknown
  isSubmitting?: boolean
}

export const CloseSurveyModal = ({
  selectedRow,
  handleCloseModal,
  onConfirm,
  isSubmitting = false
}: CloseSurveyModalProps): JSX.Element => {
  const abbreviation = selectedRow?.linkedSection?.abbreviation ?? ''
  const title = selectedRow?.linkedSection?.title ?? ''

  // Wait for the close request to resolve before dismissing the modal. Keep it open when the
  // action was blocked (onConfirm returns false) or failed (rejects) so the user can retry; the
  // mutation surfaces its own error notification and the blocked state shows its own modal.
  const handleConfirm = async (): Promise<void> => {
    try {
      const proceeded = await onConfirm(selectedRow)
      if (proceeded !== false) handleCloseModal?.()
    } catch {
      // Intentionally left blank — error feedback is handled by the mutation's onError.
    }
  }

  return (
    <Box>
      <Alert severity='error' icon={<ErrorOutlineIcon />} sx={{ mb: 3 }}>
        <Typography fontWeight='bold' fontSize='0.875rem'>
          The survey will stop accepting any further student responses immediately.
        </Typography>
      </Alert>

      {(abbreviation !== '' || title !== '') && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant='body2' color='text.secondary'>Course Section</Typography>
            <Typography variant='body2' fontWeight={500}>{abbreviation}</Typography>
            <Typography variant='body2' color='text.secondary'>Title</Typography>
            <Typography variant='body2' fontWeight={500}>{title}</Typography>
          </Box>
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      <Typography variant='body2' fontWeight={500} sx={{ mb: 1 }}>
        Closing this survey will:
      </Typography>

      <List dense disablePadding sx={{ mb: 2 }}>
        <ListItem disableGutters sx={{ alignItems: 'flex-start', gap: 1 }}>
          <Typography component='span' sx={{ mt: '3px', lineHeight: 1, flexShrink: 0 }}>•</Typography>
          <ListItemText
            primary='Stop the survey from accepting any further student responses.'
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters sx={{ alignItems: 'flex-start', gap: 1 }}>
          <Typography component='span' sx={{ mt: '3px', lineHeight: 1, flexShrink: 0 }}>•</Typography>
          <ListItemText
            primary='Generate the course section report immediately.'
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Typography variant='body2' sx={{ mb: 3 }}>
        Are you sure you want to proceed?
      </Typography>

      <DefaultModalActions
        destructiveButtonProps={{ onClick: handleCloseModal }}
        confirmButtonProps={{
          onClick: () => { void handleConfirm() },
          text: 'Close Survey',
          buttonType: ButtonType.Destructive,
          loading: isSubmitting
        }}
      />
    </Box>
  )
}
